import { useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../../theme";
import VerifiedIcon from "@mui/icons-material/Verified";
import Header from "../../../Dashboard/Header";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputLabel,
  Tooltip,
  Select,
  FormControl,
  MenuItem,
} from "@mui/material";
import Swal from "sweetalert2";
import ShowVoucherDetails from "./ShowVoucherDetails";

const PREFERENCE_KEY = "infiniteAppUserPreferences";
const SELECTED_VCH_TYPE_KEY = "selectedVchTypeFinalApproval";

// Add CSS for SweetAlert container positioning
const style = document.createElement('style');
style.textContent = `
  .swal-container-show-voucher-details {
    position: fixed;
    z-index: 10000;
  }
  
  .swal-container-final-approval-table {
    position: fixed;
    z-index: 10000;
  }
`;
document.head.appendChild(style);

const FinalVchApprov = () => {
  const [FinalVchs, setVouchers] = useState([]);
  const [allVouchers, setAllVouchers] = useState([]); // Store all vouchers for reference
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");
  const [selectedIds, setSelectedIds] = useState([]);
  const [rejectId, setrejectId] = useState("");
  const [rejectReason, setrejectReason] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [bulkReject, setBulkReject] = useState(false);
  const [openPeriod, setOpenPeriod] = useState(false);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [VchTypes, setVchTypes] = useState([]);

  // Ref for the text field to focus on dialog open
  const remarkTextFieldRef = useRef(null);

  // Load saved user preferences from localStorage
  const saved = localStorage.getItem(PREFERENCE_KEY);
  const preferences = saved
    ? JSON.parse(saved)
    : {
        rowHeight: 40,
        fontFamily: "Source Sans Pro",
        fontSize: 16,
        tableAlign: "center",
      };

  // Initialize vchTypeName from localStorage or empty string
  const [vchTypeName, setvchTypeName] = useState(() => {
    const savedVchType = localStorage.getItem(SELECTED_VCH_TYPE_KEY);
    return savedVchType || "";
  });

  // Function to get next available voucher type when current type is empty
  const getNextVoucherType = (currentType, availableTypes) => {
    if (!currentType || availableTypes.length === 0) return "";
    
    const currentIndex = availableTypes.findIndex(type => type.vch_type === currentType);
    if (currentIndex === -1) return availableTypes[0]?.vch_type || "";
    
    // Try to get next type in the array
    for (let i = currentIndex + 1; i < availableTypes.length; i++) {
      return availableTypes[i].vch_type;
    }
    
    // If no next type, try previous types
    for (let i = 0; i < currentIndex; i++) {
      return availableTypes[i].vch_type;
    }
    
    return ""; // No other types available
  };

  // Function to check and switch voucher type if current type is empty
  const checkAndSwitchVoucherType = (currentVouchers, currentType, allVoucherTypes) => {
    // If no filter is applied (showing all), don't switch
    if (!currentType || currentType === "") return currentType;
    
    // If current type has no vouchers, find another type with vouchers
    if (currentVouchers.length === 0 && allVoucherTypes.length > 0) {
      const nextType = getNextVoucherType(currentType, allVoucherTypes);
      if (nextType) {
        // Update state and localStorage
        setvchTypeName(nextType);
        localStorage.setItem(SELECTED_VCH_TYPE_KEY, nextType);
        return nextType;
      } else {
        // No other types with vouchers, clear the filter
        setvchTypeName("");
        localStorage.removeItem(SELECTED_VCH_TYPE_KEY);
        return "";
      }
    }
    
    return currentType;
  };

  const handleVchTypeChange = (event) => {
    const value = event.target.value;
    setvchTypeName(value);
    localStorage.setItem(SELECTED_VCH_TYPE_KEY, value);
    filterVouchersByType(value);
  };

  // Filter vouchers by type
  const filterVouchersByType = (type) => {
    if (!type) {
      setVouchers(allVouchers);
    } else {
      const filtered = allVouchers.filter(row => row.vch_type === type);
      setVouchers(filtered);
    }
  };

  // Clear voucher type filter
  const clearVchTypeFilter = () => {
    setvchTypeName("");
    localStorage.removeItem(SELECTED_VCH_TYPE_KEY);
    filterVouchersByType("");
  };

  // Function to show SweetAlert above specific container
  const showSweetAlert = (config, containerId = null) => {
    const swalConfig = {
      ...config,
      customClass: {
        popup: "small-alert",
        container: containerId ? `swal-container-${containerId}` : "",
      },
    };

    return Swal.fire(swalConfig);
  };

  // Function to fetch Vouchers Data
  const fetchData = async (selectedType = vchTypeName) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/approvalModule/vchFinalList",
        { cmpid },
        {
          headers: { Authorization: token },
        }
      );
      const FinalArrayData = Object.values(response.data);
      
      // Sort voucher data by voucher number in ascending order initially
      const sortedFinalArrayData = FinalArrayData.sort((a, b) => {
        const vchNumA = parseInt(a.vch_number) || 0;
        const vchNumB = parseInt(b.vch_number) || 0;
        return vchNumA - vchNumB;
      });

      // Store all vouchers
      setAllVouchers(sortedFinalArrayData);

      // Get unique voucher types
      const uniqueVchtype = sortedFinalArrayData.filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.vch_type === item.vch_type)
      );
      setVchTypes(uniqueVchtype);

      // Apply initial filter
      if (!selectedType) {
        setVouchers(sortedFinalArrayData);
      } else {
        const vchtypeFlt = sortedFinalArrayData.filter(
          (row) => row.vch_type === selectedType
        );
        setVouchers(vchtypeFlt);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Enhanced function to handle approval with auto-switching
  const handleApprovalWithSwitch = async (id, status, remark = "") => {
    try {
      // Call the approval API
      await axios.post(
        process.env.REACT_APP_API_URL + "/approvalModule/vchFinalUpdate",
        { 
          vchid: id, 
          status, 
          rejectionremark: remark 
        },
        {
          headers: { Authorization: token },
        }
      );

      // Remove the approved voucher from allVouchers
      const updatedAllVouchers = allVouchers.filter(vch => vch.vch_id !== id);
      setAllVouchers(updatedAllVouchers);

      // Get current voucher type
      const currentType = vchTypeName;
      
      // Filter vouchers by current type
      let filteredVouchers = updatedAllVouchers;
      if (currentType) {
        filteredVouchers = updatedAllVouchers.filter(row => row.vch_type === currentType);
      }
      
      // Check if we need to switch voucher type
      if (currentType && filteredVouchers.length === 0 && updatedAllVouchers.length > 0) {
        const uniqueVchTypes = updatedAllVouchers.filter(
          (item, index, self) =>
            index === self.findIndex((obj) => obj.vch_type === item.vch_type)
        );
        
        const nextType = getNextVoucherType(currentType, uniqueVchTypes);
        
        if (nextType) {
          // Switch to next available voucher type
          setvchTypeName(nextType);
          localStorage.setItem(SELECTED_VCH_TYPE_KEY, nextType);
          
          // Show success message and reload with new type
          showSweetAlert({
            title: "Approved!",
            text: `Voucher approved. Switching to ${nextType} vouchers.`,
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#28a745",
            width: "300px",
            padding: "1em",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
          }, "final-approval-table").then((result) => {
            if (result.isConfirmed) {
              // Filter with new type
              const newFiltered = updatedAllVouchers.filter(row => row.vch_type === nextType);
              setVouchers(newFiltered);
            }
          });
        } else {
          // No other voucher types, show all
          setvchTypeName("");
          localStorage.removeItem(SELECTED_VCH_TYPE_KEY);
          setVouchers(updatedAllVouchers);
          
          showSweetAlert({
            title: "Approved!",
            text: "Voucher approved. Showing all voucher types.",
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#28a745",
            width: "300px",
            padding: "1em",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
          }, "final-approval-table").then((result) => {
            if (result.isConfirmed) {
              // Already updated state above
            }
          });
        }
      } else {
        // Just update the current view
        setVouchers(filteredVouchers);
        
        // Show success message
        showSweetAlert({
          title: "Approved!",
          text: "Voucher Entry Approved successfully.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#28a745",
          width: "300px",
          padding: "1em",
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        }, "final-approval-table").then((result) => {
          if (result.isConfirmed) {
            // State already updated
          }
        });
      }
    } catch (error) {
      showSweetAlert({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonText: "Close",
        confirmButtonColor: "red",
        width: "300px",
        padding: "1em",
      }, "final-approval-table");
    }
  };

  // Function to handle approval from ShowVoucherDetails
  const handleVoucherApprove = async (vchId) => {
    await handleApprovalWithSwitch(vchId, 1, "");
  };

  // Function to handle rejection from ShowVoucherDetails
  const handleVoucherReject = async (vchId, rejectReason) => {
    await handleApprovalWithSwitch(vchId, 0, rejectReason);
  };

  // Single approval handler for table
  const handleApproval = (id, status) => {
    const remark = status === "0" ? rejectReason : "";
    handleApprovalWithSwitch(id, parseInt(status), remark);
    
    if (status === "0") {
      setrejectReason("");
      handleClose();
    }
  };

  // Bulk approval handler
  const processBulkApproval = async (selectedVoucherIds, status, rejectionRemark = "") => {
    const formattedArray = selectedVoucherIds.map((id) => ({
      vchid: id,
      status,
      rejectionremark: rejectionRemark,
    }));

    try {
      await axios.post(
        process.env.REACT_APP_API_URL + "/approvalModule/allVchFinalUpdate",
        formattedArray,
        {
          headers: { Authorization: token },
        }
      );

      // Remove approved vouchers from allVouchers
      const updatedAllVouchers = allVouchers.filter(
        vch => !selectedVoucherIds.includes(vch.vch_id)
      );
      setAllVouchers(updatedAllVouchers);

      // Get current voucher type
      const currentType = vchTypeName;
      
      // Filter vouchers by current type
      let filteredVouchers = updatedAllVouchers;
      if (currentType) {
        filteredVouchers = updatedAllVouchers.filter(row => row.vch_type === currentType);
      }
      
      // Check if we need to switch voucher type after bulk operation
      if (currentType && filteredVouchers.length === 0 && updatedAllVouchers.length > 0) {
        const uniqueVchTypes = updatedAllVouchers.filter(
          (item, index, self) =>
            index === self.findIndex((obj) => obj.vch_type === item.vch_type)
        );
        
        const nextType = getNextVoucherType(currentType, uniqueVchTypes);
        
        if (nextType) {
          setvchTypeName(nextType);
          localStorage.setItem(SELECTED_VCH_TYPE_KEY, nextType);
          
          const newFiltered = updatedAllVouchers.filter(row => row.vch_type === nextType);
          setVouchers(newFiltered);
          
          showSweetAlert({
            title: status === 1 ? "Approved!" : "Rejected!",
            text: status === 1 
              ? `Vouchers approved. Switching to ${nextType} vouchers.`
              : `Vouchers rejected. Switching to ${nextType} vouchers.`,
            icon: status === 1 ? "success" : "error",
            confirmButtonText: "OK",
            confirmButtonColor: status === 1 ? "#28a745" : "red",
            width: "300px",
            padding: "1em",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
          }, "final-approval-table");
        } else {
          setvchTypeName("");
          localStorage.removeItem(SELECTED_VCH_TYPE_KEY);
          setVouchers(updatedAllVouchers);
          
          showSweetAlert({
            title: status === 1 ? "Approved!" : "Rejected!",
            text: status === 1 
              ? "Vouchers approved. Showing all voucher types."
              : "Vouchers rejected. Showing all voucher types.",
            icon: status === 1 ? "success" : "error",
            confirmButtonText: "OK",
            confirmButtonColor: status === 1 ? "#28a745" : "red",
            width: "300px",
            padding: "1em",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
          }, "final-approval-table");
        }
      } else {
        setVouchers(filteredVouchers);
        
        showSweetAlert({
          title: status === 1 ? "Approved!" : "Rejected!",
          text: status === 1 
            ? "Vouchers approved successfully."
            : "Vouchers rejected successfully.",
          icon: status === 1 ? "success" : "error",
          confirmButtonText: "OK",
          confirmButtonColor: status === 1 ? "#28a745" : "red",
          width: "300px",
          padding: "1em",
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        }, "final-approval-table");
      }
    } catch (error) {
      showSweetAlert({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonText: "Close",
        confirmButtonColor: "red",
        width: "300px",
        padding: "1em",
      }, "final-approval-table");
    }
  };

  // Rest of the code remains the same (convertDate, hnadleReject, handleApprovalAll, etc.)
  const convertDate = (dateString) => {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${day} /${month} /${year}`;
  };

  const hnadleReject = (id) => {
    setrejectId(id);
    setBulkReject(false);
    setOpen(true);
  };

  const handleApprovalAll = (status) => {
    const selectedVoucherIds = FinalVchsRow.filter((FinalVchsRow) =>
      selectedIds.includes(FinalVchsRow.id)
    ).map((FinalVchsRow) => FinalVchsRow.id);

    if (selectedVoucherIds.length === 0) {
      showSweetAlert({
        title: "Warning",
        text: "Please select check box for Multiple Approval/Rejection.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#f0ad4e",
        width: "300px",
        padding: "1em",
      }, "final-approval-table");
      return;
    }

    if (status === 0) {
      setBulkReject(true);
      setOpen(true);
    } else {
      processBulkApproval(selectedVoucherIds, status);
    }
  };

  // Filter data initial state
  const [filteredData, setFilteredData] = useState([]);

  const handleApply = () => {
    let result = FinalVchs;
    
    if (fromDate || toDate) {
      result = result.filter((row) => {
        const dateStr = row.vch_date.toString();
        const rowDate = new Date(
          `${dateStr.substring(0, 4)}-${dateStr.substring(
            4,
            6
          )}-${dateStr.substring(6, 8)}`
        );

        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;

        if (from && to) return rowDate >= from && rowDate <= to;
        if (from) return rowDate >= from;
        if (to) return rowDate <= to;
        return true;
      });
    } else {
      result = FinalVchs;
    }

    setFilteredData(result);
    setOpenPeriod(false);
  };

  const DateFilterData = !fromDate && !toDate ? FinalVchs : filteredData;

  // Rows for voucher tables
  const FinalVchsRow = Array.isArray(DateFilterData)
    ? DateFilterData.map((FinalVch) => ({
        id: FinalVch.vch_id,
        vchDate: convertDate(FinalVch.vch_date),
        partyName: FinalVch.PartyName,
        vchNumber: FinalVch.vch_number,
        vchTypes: FinalVch.vch_type,
        vchAmount: FinalVch.vch_Amount,
      }))
    : [];

  const getColumnWidth = (FinalVchsRow, field, header) => {
    const maxLength = Math.max(
      ...FinalVchsRow.map((r) => (r[field] ? r[field].toString().length : 0)),
      header.length
    );
    return maxLength * 10 + 15;
  };

  const FinalVchColumns = [
    {
      field: "vchDate",
      headerName: "Date",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      width: getColumnWidth(FinalVchsRow, "vchDate", "Date"),
    },
    {
      field: "partyName",
      headerName: "Particulars",
      headerAlign: "left",
      align: "left",
      width: Math.min(getColumnWidth(FinalVchsRow, "partyName", "Particulars"), 200),
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "vchNumber",
      headerName: "Vch No",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      width: getColumnWidth(FinalVchsRow, "vchNumber", "Vch No") + 50,
      sortable: true,
    },
    {
      field: "vchAmount",
      headerName: "Amount",
      headerAlign: "right",
      align: "right",
      width: getColumnWidth(FinalVchsRow, "vchAmount", "Amount"),
      sortable: true,
    },
    {
      field: "vchTypes",
      headerName: "Vch Types",
      headerAlign: "left",
      align: "left",
      width: Math.min(getColumnWidth(FinalVchsRow, "vchTypes", "Vch Types"), 150),
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "action",
      headerName: "",
      headerAlign: preferences.tableAlign,
      align: preferences.tableAlign,
      headerClassName: "sticky-col-header",
      cellClassName: "sticky-col",
      width: 250,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="success"
            style={{ height: 20, fontSize: 15 }}
            startIcon={<VerifiedIcon />}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleApproval(params.id, "1");
            }}
          >
            Approve
          </Button>
  
          <Button
            variant="contained"
            color="error"
            style={{
              height: 20,
            }}
            startIcon={<CancelRoundedIcon />}
            type="button"
            id="Rejected"
            onClick={(e) => {
              e.stopPropagation();
              hnadleReject(params.id);
            }}
          >
            Reject
          </Button>
        </Box>
      ),
    },
  ];

  // Dialog state and handlers
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setrejectReason("");
    setBulkReject(false);
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      if (remarkTextFieldRef.current) {
        remarkTextFieldRef.current.focus();
      }
    }, 100);
  };

  // Code for single voucher data show
  const [openView, setViewOpen] = useState(false);
  const [vchId, setvchId] = useState("");

  const handleCloseShow = () => setViewOpen(false);

  const handleShowData = async (id) => {
    setViewOpen(true);
    setvchId(id);
  };

  // Load data on component mount
  useEffect(() => {
    const savedVchType = localStorage.getItem(SELECTED_VCH_TYPE_KEY);
    if (savedVchType) {
      setvchTypeName(savedVchType);
    }
    fetchData(savedVchType || "");
  }, []);

  return (
    <>
      <ShowVoucherDetails
        openView={openView}
        vchId={vchId}
        handleCloseShow={handleCloseShow}
        showApproveButton={true}
        showRejectButton={true}
        onApprove={handleVoucherApprove}
        onReject={handleVoucherReject}
      />

      {/* Reject Reason Dialog */}
      <Box>
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: "400px",
              maxWidth: "50%",
              height: "250px",
            },
          }}
          TransitionProps={{
            onEntered: () => {
              setTimeout(() => {
                if (remarkTextFieldRef.current) {
                  remarkTextFieldRef.current.focus();
                }
              }, 100);
            }
          }}
        >
          <DialogTitle variant="h3">
            {bulkReject
              ? "Add Rejection Remark for All Selected"
              : "Add Reason for Reject"}
          </DialogTitle>

          <DialogContent>
            <Box component="form">
              <TextField
                size="small"
                variant="outlined"
                label="Enter Reason"
                fullWidth
                margin="normal"
                value={rejectReason}
                onChange={(event) => setrejectReason(event.target.value)}
                inputRef={remarkTextFieldRef}
                autoFocus
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                if (bulkReject) {
                  const selectedVoucherIds = FinalVchsRow.filter((row) =>
                    selectedIds.includes(row.id)
                  ).map((row) => row.id);
                  processBulkApproval(selectedVoucherIds, 0, rejectReason);
                } else {
                  handleApproval(rejectId, "0");
                }
                handleClose();
              }}
              variant="contained"
              sx={{
                mr: 2,
                mb: 2,
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[150],
                "&:hover": {
                  backgroundColor: colors.blueAccent[700],
                  color: colors.primary[150],
                },
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Main Content */}
      <Box mt="70px" ml="0px" mr="20px" id="final-approval-table">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Voucher Approval" subtitle="Final Voucher Approval" />
          
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setOpenPeriod(true)}
              variant="contained"
              sx={{
                height: "35px",
                minHeight: "35px",
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[150],
                "&:hover": {
                  backgroundColor: colors.blueAccent[700],
                  color: colors.primary[150],
                },
              }}
            >
              {!fromDate && !toDate ? (
                <>
                  <span>From :- </span>
                  {FinalVchs?.length > 0 && FinalVchs[0]?.vch_date
                    ? convertDate(FinalVchs[0].vch_date)
                    : "—"}
                </>
              ) : (
                <>
                  <span>From :- </span>
                  {fromDate || "—"},<span>To :- </span>
                  {toDate || "—"}
                </>
              )}
            </Button>

            {/* Voucher type filter */}
            <FormControl
              variant="outlined"
              sx={{
                minWidth: 200,
                height: "35px",
                minHeight: "35px",
              }}
            >
              <InputLabel
                id="vch-type-label"
                sx={{
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: "translate(14px, 0px) scale(1)",
                  "&.Mui-focused, &.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              >
                Select Vch Type
              </InputLabel>
              <Select
                labelId="vch-type-label"
                value={vchTypeName}
                onChange={handleVchTypeChange}
                label="Select Vch Type"
                sx={{
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                  "& .MuiSelect-select": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Voucher Types</em>
                </MenuItem>
                {VchTypes?.map((item, index) => (
                  <MenuItem key={index} value={item.vch_type}>
                    {item.vch_type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              color="success"
              sx={{
                height: "30px",
                minHeight: "30px",
              }}
              onClick={() => handleApprovalAll(1)}
            >
              Approve All
            </Button>
            <Button
              variant="contained"
              color="error"
              sx={{
                height: "30px",
                minHeight: "30px",
              }}
              onClick={() => handleApprovalAll(0)}
            >
              Reject All
            </Button>
          </Box>
        </Box>

        <Box
          height="81vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${colors.grey[700]} !important`,
              cursor: "pointer",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
              color: colors.primary[150],
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
              color: colors.primary[150],
            },
            "& .MuiDataGrid-cell:focus": { outline: "none" },
            "& .MuiDataGrid-row": {
              borderBottom: `1px solid ${colors.grey[700]} !important`,
            },
            "& .Finalzoom-row:hover": {
              transform: "scale(1.01)",
              transition: "transform 0.2s ease-in-out",
              zIndex: 1,
              backgroundColor: colors.blueAccent[900],
            },
            "& .sticky-col-header": {
              position: "sticky",
              right: 0,
              zIndex: 2,
              backgroundColor: colors.blueAccent[700],
            },
            "& .sticky-col": {
              position: "sticky",
              right: 0,
              zIndex: 1,
              backgroundColor: colors.primary[400],
            },
            "& .Finalzoom-row:hover .sticky-col": {
              backgroundColor: colors.blueAccent[900],
            },
          }}
        >
          <DataGrid
            rows={FinalVchsRow}
            columns={FinalVchColumns}
            pageSize={pageSize}
            onRowClick={(params) => handleShowData(params.row.id)}
            getRowClassName={() => "Finalzoom-row"}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[20, 50, 100]}
            pagination
            rowHeight={30}
            headerHeight={30}
            componentsProps={{
              footer: {
                sx: {
                  height: 30,
                  minHeight: 30,
                },
              },
            }}
            checkboxSelection
            onSelectionModelChange={(newSelection) =>
              setSelectedIds(newSelection)
            }
            sx={{
              "& .MuiDataGrid-virtualScroller": {
                overflow: "auto",
              },
              "& .MuiDataGrid-menuIcon, & .MuiDataGrid-sortIcon": {
                opacity: 0,
                transition: "opacity 0.2s ease-in-out",
              },
              "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon":
                {
                  opacity: 1,
                },
            }}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: "vchNumber",
                    sort: "asc",
                  },
                ],
              },
            }}
          />
        </Box>
      </Box>

      {/* Change Period Dialog */}
      <Dialog open={openPeriod} onClose={() => setOpenPeriod(false)}>
        <DialogTitle variant="h2">Change Period</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography sx={{ minWidth: "50px" }}>From :</Typography>
              <TextField
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                size="small"
              />
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography sx={{ minWidth: "50px" }}>To :</Typography>
              <TextField
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenPeriod(false);
              setFromDate("");
              setToDate("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              backgroundColor: "#EFE17C",
              color: "#000000",
              height: "35px",
              minHeight: "35px",
              "&:hover": {
                backgroundColor: "#E6D669",
                color: "#000000",
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinalVchApprov;